/**
 * Source decoding: sensor data in, scene-linear RGB out.
 *
 * Three paths, one output contract:
 *  - RAW (CR3/CR2/NEF/ARW/DNG/RAF/ORF/…) through LibRaw compiled to WebAssembly,
 *    asked for 16-bit **linear** sRGB with the camera's own white balance and no
 *    auto-brightening, so the app receives scene-referred values rather than a
 *    pre-baked JPEG look;
 *  - TIFF, including the 16-bit files that come out of other raw converters,
 *    read at full bit depth and linearised from sRGB;
 *  - ordinary bitmaps through the browser's own decoder.
 *
 * The source buffer is never written back. Everything here is read-only.
 */

import UTIF from 'utif';
import {
  type CaptureFacts,
  type DecodedImage,
  type SourceKind,
  classifySource,
  emptyCaptureFacts,
  estimateAsShotWhiteBalance,
} from '@lumos/core';

export interface DecodeOptions {
  filename: string;
  maxWorkingEdge: number;
  full: boolean;
  onProgress?: (stage: string, message: string) => void;
}

export async function decodeSource(
  buffer: ArrayBuffer,
  options: DecodeOptions,
): Promise<DecodedImage> {
  const kind: SourceKind = classifySource(options.filename) ?? sniffKind(buffer);
  switch (kind) {
    case 'raw':
      return decodeRaw(buffer, options);
    case 'tiff':
      return decodeTiff(buffer, options);
    default:
      return decodeBitmap(buffer, options);
  }
}

/** Last-resort format detection when the filename has no useful extension. */
function sniffKind(buffer: ArrayBuffer): SourceKind {
  const bytes = new Uint8Array(buffer, 0, Math.min(16, buffer.byteLength));
  // TIFF magic: "II*\0" or "MM\0*".
  if ((bytes[0] === 0x49 && bytes[1] === 0x49 && bytes[2] === 0x2a) ||
      (bytes[0] === 0x4d && bytes[1] === 0x4d && bytes[3] === 0x2a)) {
    return 'tiff';
  }
  // ISO base media (CR3, HEIF) — "ftyp" at offset 4.
  if (bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) {
    return 'raw';
  }
  return 'bitmap';
}

// ---------------------------------------------------------------------------
// RAW
// ---------------------------------------------------------------------------

type LibRawModule = typeof import('libraw-wasm');

let librawModule: LibRawModule | null = null;

async function loadLibRaw(): Promise<LibRawModule> {
  if (!librawModule) librawModule = await import('libraw-wasm');
  return librawModule;
}

async function decodeRaw(buffer: ArrayBuffer, options: DecodeOptions): Promise<DecodedImage> {
  options.onProgress?.('decoding', 'Reading sensor data');
  const { default: LibRaw } = await loadLibRaw();
  const raw = new LibRaw();
  const warnings: string[] = [];

  try {
    // Half-size demosaic is a genuine half-resolution read of the Bayer grid,
    // not a downscale of a full decode: for a 24 MP file it is roughly 4x
    // faster and still comfortably above any screen's pixel count.
    const halfSize = !options.full;

    await raw.open(new Uint8Array(buffer), {
      useCameraWb: true,
      useCameraMatrix: 3,
      outputColor: 1, // sRGB primaries
      outputBps: 16,
      // -g 1 1 : linear transfer. The app owns the scene-to-display transform.
      gamm: [1, 1],
      noAutoBright: true,
      halfSize,
      // AHD-quality demosaic for the export path; the faster VNG is plenty for
      // a half-size interactive read.
      userQual: options.full ? 3 : 2,
      highlight: 0,
      userFlip: -1,
    });

    const meta = await raw.metadata(true);
    if (!meta) throw new Error('LibRaw returned no metadata for this file.');

    options.onProgress?.('decoding', `${meta.camera_make ?? ''} ${meta.camera_model ?? ''}`.trim() || 'Demosaicing');

    const image = await raw.imageData();
    if (!image || !image.data || image.data.length === 0) {
      throw new Error('LibRaw could not produce image data for this file.');
    }

    options.onProgress?.('converting', 'Building working image');

    const channels = image.colors ?? 3;
    const scale = image.bits === 16 ? 1 / 65535 : 1 / 255;
    const linear = toLinearRgb(image.data, image.width, image.height, channels, scale, false);

    const working = resampleLinear(
      linear,
      image.width,
      image.height,
      options.full ? Number.POSITIVE_INFINITY : options.maxWorkingEdge,
    );

    const capture = captureFromLibRaw(meta);
    const asShot = estimateAsShotWhiteBalance(
      meta.color_data?.cam_mul as number[] | undefined,
      meta.color_data?.cam_xyz as number[][] | undefined,
    );
    if (!asShot.estimated) {
      warnings.push('Camera white-balance matrices were unavailable; temperature is shown relative to a 5500 K assumption.');
    }
    if (halfSize) {
      warnings.push('Editing a half-resolution read of the sensor. Export re-decodes at full resolution.');
    }

    return {
      data: working.data,
      width: working.width,
      height: working.height,
      fullWidth: halfSize ? (meta.width ?? image.width) : image.width,
      fullHeight: halfSize ? (meta.height ?? image.height) : image.height,
      kind: 'raw',
      fromEmbeddedPreview: false,
      capture,
      asShot,
      warnings,
    };
  } catch (error) {
    // A camera whose compression this LibRaw build cannot read still has a
    // full-size JPEG inside it. Showing that, clearly labelled, is far better
    // than showing nothing — and it is exactly the degraded mode the design
    // calls for.
    const preview = await tryEmbeddedPreview(raw, options).catch(() => null);
    if (preview) {
      preview.warnings.unshift(
        `Sensor data could not be decoded (${describeError(error)}). Showing the camera's embedded preview instead, so edits apply to that JPEG rather than to the raw file.`,
      );
      return preview;
    }
    throw new Error(`Could not decode this raw file: ${describeError(error)}`);
  } finally {
    raw.dispose();
  }
}

async function tryEmbeddedPreview(
  raw: { thumbnailData: () => Promise<{ data: Uint8Array; width: number; height: number; format: string } | undefined>; metadata: (full?: boolean) => Promise<Record<string, unknown> | undefined> },
  options: DecodeOptions,
): Promise<DecodedImage | null> {
  const thumb = await raw.thumbnailData();
  if (!thumb || !thumb.data || thumb.format !== 'jpeg') return null;

  const bitmap = await createImageBitmap(new Blob([thumb.data as BlobPart], { type: 'image/jpeg' }));
  const rgba = bitmapToRgba(bitmap);
  bitmap.close();

  const linear = toLinearRgb(rgba.data, rgba.width, rgba.height, 4, 1 / 255, true);
  const working = resampleLinear(linear, rgba.width, rgba.height, options.maxWorkingEdge);

  const meta = await raw.metadata(true).catch(() => undefined);

  return {
    data: working.data,
    width: working.width,
    height: working.height,
    fullWidth: rgba.width,
    fullHeight: rgba.height,
    kind: 'raw',
    fromEmbeddedPreview: true,
    capture: meta ? captureFromLibRaw(meta) : emptyCaptureFacts(),
    asShot: { kelvin: 5500, tint: 0, estimated: false },
    warnings: [],
  };
}

/** Canon writes "Canon EOS R6m2" in Model and "Canon" in Make; joining them stutters. */
function stripRedundantMake(make: string, model: string): string {
  if (!make || !model) return model;
  const prefix = `${make} `;
  return model.toLowerCase().startsWith(prefix.toLowerCase()) ? model.slice(prefix.length) : model;
}

function captureFromLibRaw(meta: Record<string, unknown>): CaptureFacts {
  const lens = meta.lens as { Lens?: string; makernotes?: { Lens?: string } } | undefined;
  const timestamp = meta.timestamp;
  const make = String(meta.camera_make ?? '').trim();
  return {
    cameraMake: make,
    cameraModel: stripRedundantMake(make, String(meta.camera_model ?? '').trim()),
    lens: String(lens?.Lens || lens?.makernotes?.Lens || '').trim(),
    iso: Number(meta.iso_speed ?? 0) || 0,
    shutter: Number(meta.shutter ?? 0) || 0,
    aperture: Number(meta.aperture ?? 0) || 0,
    focalLength: Number(meta.focal_len ?? 0) || 0,
    capturedAt:
      timestamp instanceof Date && !Number.isNaN(timestamp.getTime()) && timestamp.getFullYear() > 1971
        ? timestamp.toISOString()
        : '',
    orientationApplied: 0,
  };
}

// ---------------------------------------------------------------------------
// TIFF
// ---------------------------------------------------------------------------

async function decodeTiff(buffer: ArrayBuffer, options: DecodeOptions): Promise<DecodedImage> {
  options.onProgress?.('decoding', 'Reading TIFF');
  const warnings: string[] = [];

  const ifds = UTIF.decode(buffer) as TiffIfd[];
  if (!ifds || ifds.length === 0) throw new Error('No image found inside the TIFF.');

  // Pick the largest sub-image: raw converters often prepend a small preview.
  const ifd = ifds.reduce((largest, candidate) => {
    const areaOf = (x: TiffIfd) => (x.width ?? 0) * (x.height ?? 0);
    return areaOf(candidate) > areaOf(largest) ? candidate : largest;
  });

  UTIF.decodeImage(buffer, ifd, ifds);

  const width = ifd.width ?? 0;
  const height = ifd.height ?? 0;
  if (width <= 0 || height <= 0) throw new Error('TIFF reported no usable dimensions.');

  const bitsPerSample = firstNumber(ifd.t258) ?? 8;
  const samplesPerPixel = firstNumber(ifd.t277) ?? 3;
  const sampleFormat = firstNumber(ifd.t339) ?? 1;
  const fileIsLittleEndian = new Uint8Array(buffer, 0, 2)[0] === 0x49;

  options.onProgress?.('converting', `${width}×${height}, ${bitsPerSample}-bit`);

  // How many source pixels collapse into one working pixel. Folding the
  // reduction into the read means a 24 MP 16-bit file never needs the ~290 MB
  // full-resolution float buffer just to fill a 2560px canvas.
  const target = options.full ? Number.POSITIVE_INFINITY : options.maxWorkingEdge;
  const decimation = Number.isFinite(target)
    ? Math.max(1, Math.floor(Math.max(width, height) / target))
    : 1;

  let linear: Float32Array;
  let linearWidth = width;
  let linearHeight = height;

  if (bitsPerSample === 16 && ifd.data) {
    // UTIF rewrites big-endian 16-bit sample data to little-endian in place
    // while decoding (see its "convert to Little Endian" step), so the buffer
    // handed back here is little-endian whatever the file's own byte order was.
    const read = tiff16ToLinear(ifd.data, width, height, samplesPerPixel, true, decimation);
    linear = read.data;
    linearWidth = read.width;
    linearHeight = read.height;
  } else if (bitsPerSample === 32 && sampleFormat === 3 && ifd.data) {
    // 32-bit float samples are left in the file's own byte order.
    const read = tiff32FloatToLinear(
      ifd.data,
      width,
      height,
      samplesPerPixel,
      fileIsLittleEndian,
      decimation,
    );
    linear = read.data;
    linearWidth = read.width;
    linearHeight = read.height;
  } else {
    // 8-bit and exotic layouts: let UTIF normalise to RGBA.
    const rgba = new Uint8ClampedArray(UTIF.toRGBA8(ifd));
    linear = toLinearRgb(rgba, width, height, 4, 1 / 255, true);
    if (bitsPerSample > 8) {
      warnings.push(`This TIFF's ${bitsPerSample}-bit layout was read through an 8-bit path; tonal headroom is reduced.`);
    }
  }

  const working = resampleLinear(linear, linearWidth, linearHeight, target);
  const capture = await readExifCapture(buffer, warnings);

  return {
    data: working.data,
    width: working.width,
    height: working.height,
    fullWidth: width,
    fullHeight: height,
    kind: 'tiff',
    fromEmbeddedPreview: false,
    capture,
    asShot: { kelvin: 5500, tint: 0, estimated: false },
    warnings,
  };
}

interface TiffIfd {
  width?: number;
  height?: number;
  data?: Uint8Array;
  t258?: number[];
  t277?: number[];
  t339?: number[];
  [key: string]: unknown;
}

function firstNumber(value: unknown): number | null {
  if (Array.isArray(value) && typeof value[0] === 'number') return value[0];
  if (typeof value === 'number') return value;
  return null;
}

interface RawPlane {
  data: Float32Array;
  width: number;
  height: number;
}

function tiff16ToLinear(
  bytes: Uint8Array,
  width: number,
  height: number,
  samplesPerPixel: number,
  littleEndian: boolean,
  decimation: number,
): RawPlane {
  const outWidth = Math.max(1, Math.floor(width / decimation));
  const outHeight = Math.max(1, Math.floor(height / decimation));
  const out = new Float32Array(outWidth * outHeight * 3);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const available = Math.floor(bytes.byteLength / 2);

  // 65536-entry sRGB table: the alternative is 72 million pow() calls.
  const lut = new Float32Array(65536);
  for (let i = 0; i < 65536; i += 1) lut[i] = srgbToLinear(i / 65535);

  const block = decimation * decimation;
  for (let y = 0; y < outHeight; y += 1) {
    for (let x = 0; x < outWidth; x += 1) {
      let r = 0;
      let g = 0;
      let b = 0;
      for (let dy = 0; dy < decimation; dy += 1) {
        const sourceY = y * decimation + dy;
        if (sourceY >= height) break;
        for (let dx = 0; dx < decimation; dx += 1) {
          const sourceX = x * decimation + dx;
          if (sourceX >= width) break;
          const sample = (sourceY * width + sourceX) * samplesPerPixel;
          if (sample + 2 >= available) continue;
          r += lut[view.getUint16(sample * 2, littleEndian)];
          g += samplesPerPixel > 1 ? lut[view.getUint16((sample + 1) * 2, littleEndian)] : 0;
          b += samplesPerPixel > 2 ? lut[view.getUint16((sample + 2) * 2, littleEndian)] : 0;
        }
      }
      const target = (y * outWidth + x) * 3;
      out[target] = r / block;
      out[target + 1] = samplesPerPixel > 1 ? g / block : out[target];
      out[target + 2] = samplesPerPixel > 2 ? b / block : out[target];
    }
  }
  return { data: out, width: outWidth, height: outHeight };
}

function tiff32FloatToLinear(
  bytes: Uint8Array,
  width: number,
  height: number,
  samplesPerPixel: number,
  littleEndian: boolean,
  decimation: number,
): RawPlane {
  const outWidth = Math.max(1, Math.floor(width / decimation));
  const outHeight = Math.max(1, Math.floor(height / decimation));
  const out = new Float32Array(outWidth * outHeight * 3);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const available = Math.floor(bytes.byteLength / 4);
  const block = decimation * decimation;

  for (let y = 0; y < outHeight; y += 1) {
    for (let x = 0; x < outWidth; x += 1) {
      let r = 0;
      let g = 0;
      let b = 0;
      for (let dy = 0; dy < decimation; dy += 1) {
        const sourceY = y * decimation + dy;
        if (sourceY >= height) break;
        for (let dx = 0; dx < decimation; dx += 1) {
          const sourceX = x * decimation + dx;
          if (sourceX >= width) break;
          const sample = (sourceY * width + sourceX) * samplesPerPixel;
          if (sample + 2 >= available) continue;
          // 32-bit float TIFF is conventionally already linear.
          r += view.getFloat32(sample * 4, littleEndian);
          g += samplesPerPixel > 1 ? view.getFloat32((sample + 1) * 4, littleEndian) : 0;
          b += samplesPerPixel > 2 ? view.getFloat32((sample + 2) * 4, littleEndian) : 0;
        }
      }
      const target = (y * outWidth + x) * 3;
      out[target] = r / block;
      out[target + 1] = samplesPerPixel > 1 ? g / block : out[target];
      out[target + 2] = samplesPerPixel > 2 ? b / block : out[target];
    }
  }
  return { data: out, width: outWidth, height: outHeight };
}

// ---------------------------------------------------------------------------
// Bitmaps
// ---------------------------------------------------------------------------

async function decodeBitmap(buffer: ArrayBuffer, options: DecodeOptions): Promise<DecodedImage> {
  options.onProgress?.('decoding', 'Reading image');
  const warnings: string[] = [];
  const blob = new Blob([buffer]);
  const bitmap = await createImageBitmap(blob).catch(() => {
    throw new Error('The browser could not decode this image format.');
  });

  const rgba = bitmapToRgba(bitmap);
  bitmap.close();

  const linear = toLinearRgb(rgba.data, rgba.width, rgba.height, 4, 1 / 255, true);
  const working = resampleLinear(
    linear,
    rgba.width,
    rgba.height,
    options.full ? Number.POSITIVE_INFINITY : options.maxWorkingEdge,
  );
  const capture = await readExifCapture(buffer, warnings);

  warnings.push('This is an 8-bit file. There is far less latitude for exposure and highlight recovery than a raw capture.');

  return {
    data: working.data,
    width: working.width,
    height: working.height,
    fullWidth: rgba.width,
    fullHeight: rgba.height,
    kind: 'bitmap',
    fromEmbeddedPreview: false,
    capture,
    asShot: { kelvin: 5500, tint: 0, estimated: false },
    warnings,
  };
}

function bitmapToRgba(bitmap: ImageBitmap): { data: Uint8ClampedArray; width: number; height: number } {
  const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('No 2D context available for image decoding.');
  context.drawImage(bitmap, 0, 0);
  const imageData = context.getImageData(0, 0, bitmap.width, bitmap.height);
  return { data: imageData.data, width: bitmap.width, height: bitmap.height };
}

async function readExifCapture(buffer: ArrayBuffer, warnings: string[]): Promise<CaptureFacts> {
  try {
    const exifr = await import('exifr');
    const tags = (await exifr.parse(buffer, {
      pick: [
        'Make',
        'Model',
        'LensModel',
        'ISO',
        'ISOSpeedRatings',
        'ExposureTime',
        'FNumber',
        'FocalLength',
        'DateTimeOriginal',
      ],
    })) as Record<string, unknown> | undefined;
    if (!tags) return emptyCaptureFacts();
    const captured = tags.DateTimeOriginal;
    const make = String(tags.Make ?? '').trim();
    return {
      cameraMake: make,
      cameraModel: stripRedundantMake(make, String(tags.Model ?? '').trim()),
      lens: String(tags.LensModel ?? '').trim(),
      iso: Number(tags.ISO ?? tags.ISOSpeedRatings ?? 0) || 0,
      shutter: Number(tags.ExposureTime ?? 0) || 0,
      aperture: Number(tags.FNumber ?? 0) || 0,
      focalLength: Number(tags.FocalLength ?? 0) || 0,
      capturedAt: captured instanceof Date ? captured.toISOString() : '',
      orientationApplied: 0,
    };
  } catch {
    warnings.push('Capture metadata could not be read from this file.');
    return emptyCaptureFacts();
  }
}

// ---------------------------------------------------------------------------
// Shared conversion
// ---------------------------------------------------------------------------

/** sRGB electro-optical transfer function: encoded value to linear light. */
export function srgbToLinear(value: number): number {
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

function toLinearRgb(
  source: Uint8Array | Uint16Array | Uint8ClampedArray,
  width: number,
  height: number,
  channels: number,
  scale: number,
  encoded: boolean,
): Float32Array {
  const pixels = width * height;
  const out = new Float32Array(pixels * 3);

  if (encoded) {
    // Build a lookup table once rather than calling pow() tens of millions of
    // times: for a 45 MP file that is the difference between ~2 s and ~90 ms.
    const levels = scale === 1 / 255 ? 256 : 65536;
    const lut = new Float32Array(levels);
    for (let i = 0; i < levels; i += 1) lut[i] = srgbToLinear(i / (levels - 1));
    for (let i = 0; i < pixels; i += 1) {
      const base = i * channels;
      out[i * 3] = lut[source[base]] ?? 0;
      out[i * 3 + 1] = lut[source[base + 1]] ?? 0;
      out[i * 3 + 2] = lut[source[base + 2]] ?? 0;
    }
    return out;
  }

  for (let i = 0; i < pixels; i += 1) {
    const base = i * channels;
    out[i * 3] = source[base] * scale;
    out[i * 3 + 1] = source[base + 1] * scale;
    out[i * 3 + 2] = source[base + 2] * scale;
  }
  return out;
}

/**
 * Box-filter downscale to the working resolution.
 *
 * Averaging every contributing source pixel (rather than sampling one of them)
 * is what keeps a 45 MP frame's noise from turning into aliased speckle on
 * screen, and it keeps the histogram honest: the working image and the export
 * agree about where the highlights are.
 */
export function resampleLinear(
  source: Float32Array,
  width: number,
  height: number,
  maxEdge: number,
): { data: Float32Array; width: number; height: number } {
  const longest = Math.max(width, height);
  if (!Number.isFinite(maxEdge) || longest <= maxEdge) {
    return { data: source, width, height };
  }

  const ratio = maxEdge / longest;
  const targetWidth = Math.max(1, Math.round(width * ratio));
  const targetHeight = Math.max(1, Math.round(height * ratio));
  const out = new Float32Array(targetWidth * targetHeight * 3);

  const xScale = width / targetWidth;
  const yScale = height / targetHeight;

  for (let y = 0; y < targetHeight; y += 1) {
    const y0 = Math.floor(y * yScale);
    const y1 = Math.min(height, Math.max(y0 + 1, Math.ceil((y + 1) * yScale)));
    for (let x = 0; x < targetWidth; x += 1) {
      const x0 = Math.floor(x * xScale);
      const x1 = Math.min(width, Math.max(x0 + 1, Math.ceil((x + 1) * xScale)));

      let r = 0;
      let g = 0;
      let b = 0;
      let n = 0;
      for (let sy = y0; sy < y1; sy += 1) {
        const rowOffset = sy * width;
        for (let sx = x0; sx < x1; sx += 1) {
          const index = (rowOffset + sx) * 3;
          r += source[index];
          g += source[index + 1];
          b += source[index + 2];
          n += 1;
        }
      }
      const target = (y * targetWidth + x) * 3;
      const inverse = n > 0 ? 1 / n : 0;
      out[target] = r * inverse;
      out[target + 1] = g * inverse;
      out[target + 2] = b * inverse;
    }
  }

  return { data: out, width: targetWidth, height: targetHeight };
}

function describeError(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}
